// Browser polyfills
import 'whatwg-fetch';

// Libs
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Layout, Result } from 'antd';
import NackleMenu from '@nackle/menu';
const { Content } = Layout;
import { GOLD_COMPANY_ID } from './utils/constants.js';
import { AppHeader, AppFooter, AppError } from '@nackle/studio-tools';

// Routing
import { Link, Route, routeHistory } from '@nackle/biw-web-router';
import { routes, bundleRoutingMiddleware } from './routing.jsx';

import { applyRequestMiddleware } from './utils/fetch';

// Bridge
import { Bridge } from '@nackle/bridge';
import auth, { authRouteMiddleware, authRequestMiddleware } from './auth.js';

//api
import { apiGetBundles } from './api.js';

import './styles/style.less';
import { NotificationContextProvider } from './context/notification-context.jsx';

/*******************************************************************************
 *  1. App (root level component)
 */

class App extends React.Component {
    state = {
        user: '',
        locale: 'en-US',
        bundlesLoaded: false
    };

    handleSignIn = async ( user ) => {
        const match = window.location.pathname.match(
            /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
        );
        if ( match?.[ 0 ] ) {
            await auth.delegate( match[ 0 ] );
        } else {
            await auth.delegate( GOLD_COMPANY_ID );
        }

        if ( user.claims ) {
            this.setState( {
                user: user,
                locale: user.claims.locale
            } );

            if (
                !window.location.pathname.includes( 'list' ) &&
                !window.location.pathname.includes( 'notification' ) &&
                !window.location.pathname.includes( 'triggers' ) &&
                !window.location.pathname.includes( 'observer' )
            ) {
                routeHistory.push( `/${ ( match && match[ 0 ] ) || GOLD_COMPANY_ID }` );
            } else {
                routeHistory.refresh();
            }

            await apiGetBundles( [ 'navigation', 'studio-in-context-help' ], this.state.locale );

            this.setState( { bundlesLoaded: true } );
        }
    };

    handleCompanyChange = async ( company ) => {
        if ( window.location.pathname.includes( 'observer' ) ) {
            routeHistory.push( `/${ company.id }/observer` );
        } else {
            routeHistory.push( `/${ company.id }` );
        }
    };

    render() {
        const user = this.state.user;
        const { locale, bundlesLoaded } = this.state;

        return (
            <AppError>
                <Route render={ ( route ) => {
                    // set the title
                    document.title = route?.match ? route.match.title : 'Notifications';

                    const companyId = route.state?.params?.companyId;

                    return (
                        <Bridge
                            auth={ auth }
                            onSignIn={ this.handleSignIn }
                        >
                            <Layout className="root-layout">
                                <AppHeader
                                    auth={ auth }
                                    companyId={ companyId }
                                    onCompanyChange={ this.handleCompanyChange }
                                />
                                <Layout className='ant-layout-has-sider'>
                                    <NackleMenu
                                        auth={ auth }
                                        cmxBundleLoaded={ bundlesLoaded }
                                        collapsible={ true }
                                    />
                                    <Layout className="page-layout">
                                        <Content className="app-content">
                                                {
                                                    route.match ?
                                                        bundlesLoaded ?
                                                            <div key={ route.state.params.companyId } className="app-container">
                                                                <div className="main-container">
                                                                    { React.cloneElement( route.component, { user, locale } ) }
                                                                </div>
                                                            </div>
                                                            : null
                                                        : (
                                                            <Result
                                                                status="404"
                                                                title="404"
                                                                subTitle="Sorry, the page you visited does not exist."
                                                                extra={ <Link to="/" className="ant-btn ant-btn-primary">Back Home</Link> }
                                                            />
                                                        )
                                                }
                                                <AppFooter />
                                        </Content>
                                    </Layout>
                                </Layout>
                            </Layout>
                        </Bridge>
                    );
                } }/>
            </AppError>
        );
    }
}

routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( [ authRouteMiddleware, bundleRoutingMiddleware ] );

/*******************************************************************************
 *  3. Connect Auth to Fetch
 */
applyRequestMiddleware( authRequestMiddleware );


/*******************************************************************************
 * 4. Render the the app
 */

const root = createRoot( document.getElementById( 'app-root' ) );

root.render(
    <NotificationContextProvider>
        <App />
    </NotificationContextProvider>,
);